import React, { useState, useEffect, useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import Confetti from 'react-confetti';
import alphabetData from '../Data/alphabetData';
import { shuffle } from '../utils/shuffle';
import { getRandomMascotQuote } from '../utils/mascotQuotes';
import useAudio from '../hooks/useAudio';
import MascotCelebration from './MascotCelebration';
import ScoreBadge from './ScoreBadge';

const UrduAlphabet = alphabetData.filter(letter => !letter.isCompound).map(l => l.harf).filter(Boolean);

const DraggableLetter = ({ letter, moveLetter }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'LETTER',
    item: { letter },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }), [letter]);

  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'LETTER',
    drop: (dragged) => moveLetter(dragged.letter, letter),
    collect: (monitor) => ({
      isOver: monitor.isOver()
    })
  }), [letter]);

  return (
    <div
      ref={node => drag(drop(node))}
      className="letter-card"
      style={{
        opacity: isDragging ? 0.5 : 1,
        backgroundColor: isOver ? '#f0f8ff' : '#fff'
      }}
    >
      {letter}
    </div>
  );
};

function AlphabetOrdering() {
  const [shuffled, setShuffled] = useState([]);
  const [isCorrect, setIsCorrect] = useState(false);
  const [quote, setQuote] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [score, setScore] = useState(0);
  const { play } = useAudio();

  const initGame = useCallback(() => {
    const subset = shuffle(UrduAlphabet).slice(0, 6);
    setShuffled(subset);
    setIsCorrect(false);
    setQuote('');
    setShowConfetti(false);
  }, []);

  useEffect(() => {
    initGame();
  }, [initGame]);

  const moveLetter = (fromLetter, toLetter) => {
    const fromIdx = shuffled.indexOf(fromLetter);
    const toIdx = shuffled.indexOf(toLetter);
    if (fromIdx === -1 || toIdx === -1) return;

    const updated = [...shuffled];
    const [moved] = updated.splice(fromIdx, 1);
    updated.splice(toIdx, 0, moved);
    setShuffled(updated);

    const original = [...updated].sort((a, b) => UrduAlphabet.indexOf(a) - UrduAlphabet.indexOf(b));
    const success = updated.every((val, i) => val === original[i]);

    if (success) {
      setIsCorrect(true);
      setScore(prev => prev + 1);
      setQuote(getRandomMascotQuote());
      setShowConfetti(true);
      play('fanfare.mp3', { interrupt: false });
    }
  };

  return (
    <div className="popup">
      <ScoreBadge score={score} />
      <h2>ترتیب دیں: حروفِ تہجی</h2>
      <div className="letter-grid">
        {shuffled.map((letter, idx) => (
          <DraggableLetter
            key={letter + idx}
            letter={letter}
            moveLetter={moveLetter}
          />
        ))}
      </div>

      {isCorrect && (
        <MascotCelebration quote={quote} onReplay={initGame} />
      )}
      {showConfetti && <Confetti />}

      <style>{`
        .popup {
         height: 90vh;
        }
        .letter-grid {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          margin: 20px 0;
        }
        .letter-card {
          width: 60px;
          height: 60px;
          border: 2px solid #ccc;
          margin: 10px;
          font-size: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
          font-family: 'Jameel Noori Nastaleeq', serif;
          cursor: move;
          transition: all 0.2s;
        }
      `}</style>
    </div>
  );
}

export default AlphabetOrdering;

